/**
 * MediaUploadSection Component
 *
 * Section for uploading video or audio files.
 * Reads state from DubbingContext — no props required.
 */

import { memo } from 'react';
import { Play, Music } from 'lucide-react';
import { MediaUploader } from '@/components/common/MediaUploader';
import { useTranslation } from '@/hooks/useTranslation';
import { useDubbing } from '../DubbingContext.context';
import type { MediaType } from '@/components/common/MediaUploader';

export const MediaUploadSection = memo(function MediaUploadSection() {
  const { t } = useTranslation();
  const { state, isUploading, isDubbing, handleMediaSelect, handleMediaRemove } = useDubbing();
  const { mediaType, uploadProgress } = state;

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-4 xs:p-6">
      <h3 className="text-base xs:text-lg font-semibold text-gray-900 dark:text-white mb-3 xs:mb-4 flex items-center space-x-2">
        {mediaType === 'audio' ? (
          <Music className="h-4 w-4 xs:h-5 xs:w-5" />
        ) : (
          <Play className="h-4 w-4 xs:h-5 xs:w-5" />
        )}
        <span>{t('dubbing.sections.uploadMedia', { defaultValue: 'Upload Video or Audio' })}</span>
      </h3>
      <MediaUploader
        accept={['video', 'audio']}
        onFileSelect={(file, type) => handleMediaSelect(file, type as MediaType)}
        onRemove={handleMediaRemove}
        disabled={isUploading || isDubbing}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        translationNamespace="dubbing"
      />
    </div>
  );
});
